import React from 'react'
import PropTypes from 'prop-types'
import gql from 'graphql-tag'
import {Redirect} from 'react-router-dom'
import moment from 'moment'
import debounce from 'lodash.debounce'
import {firstVisibleDay, lastVisibleDay} from 'react-big-calendar/lib/utils/dates'
import Layout from 'components/PrivateRoutes/Layout'
import {QueryParams, BookingsCalendar as Calendar} from 'components/shared'
import Query from 'shared/components/Query'

const BookingsCalendarQuery = gql`
  query BookingsCalendarQuery($after: DateTime, $before: DateTime) {
    serviceProvider {
      id
      bookings(endsAfterDateTime: $after, startsBeforeDateTime: $before) {
        edges {
          node {
            id
            state
            startsAt
            endsAt
            premiumService
            estate {
              id
              invisoRef
              streetAddress
              postalArea
              postalCode
              broker {
                id
                phone
              }
            }
            products {
              edges {
                node {
                  id
                  name
                  state
                  ... on PhotoBasedProduct { quantity }
                  ... on MeasurementProduct { quantity }
                }
              }
            }
          }
        }
      }
      availability(endsAfterDateTime: $after, startsBeforeDateTime: $before) {
        edges {
          node {
            id
            startsAt
            endsAt
          }
        }
      }
    }
  }
`

class BookingsCalendar extends React.Component {
  state = {}

  debouncedSetQueryParams = () => {
    this.setQueryParams(this.state)
  }

  setQueryParam = (param) => {
    this.setState(param, () => this.debouncedSetQueryParams())
  }

  debouncedSetQueryParams = debounce(this.debouncedSetQueryParams, 10)

  onSelectEvent = ({booking}) => {
    this.setState({currentBooking: booking})
  }

  render() {
    const {location} = this.props
    const {currentBooking} = this.state

    if (currentBooking) {
      const backTo = [location.pathname, location.search].join('')

      return (
        <Redirect to={{
          pathname: `/bookings/${currentBooking.id}`,
          state: {
            backTo,
          }
        }} />
      )
    }

    return (
      <Layout
        {...this.props}
        title="Kalender"
        defaultBackTo="/bookings"
      >
        <QueryParams>
          {({getQueryParam, setQueryParams}) => {
            // This might look a little weird I know,
            // but since the calendar triggers two simultaneous callbacks
            // to change both view and date when choosing a day we need some kind
            // of a debouncer to make sure both values are stored in the url.
            // This setter is therefore stored on the class so it can be picked
            // up later by another class method handling the debounce.
            this.setQueryParams = setQueryParams

            const date = moment(
              getQueryParam('date', moment().format())
            )

            const after = firstVisibleDay(date.toDate(), Calendar.localizer)
            const before = lastVisibleDay(date.toDate(), Calendar.localizer)

            return (
              <Query
                query={BookingsCalendarQuery}
                variables={{
                  after,
                  before,
                }}
              >
                {({getData, loading}) => {
                  const bookings = getData('serviceProvider.bookings.edges', [])
                  const availability = getData('serviceProvider.availability.edges', [])

                  return (
                    <Calendar
                      isFetchingData={loading}
                      view={getQueryParam('view', 'month')}
                      date={date.toDate()}
                      onView={(view) => this.setQueryParam({view})}
                      onNavigate={(date) => this.setQueryParam({date: moment(date).format()})}
                      onSelectEvent={this.onSelectEvent}
                      bookings={bookings}
                      availability={availability}
                    />
                  )
                }}
              </Query>
            )
          }}
        </QueryParams>
      </Layout>
    )
  }
}

BookingsCalendar.propTypes = {
  location: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
}

export default BookingsCalendar
